import { AsyncPipe, JsonPipe } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Timestamp } from '@angular/fire/firestore';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { Observable, of, Subject, take, takeUntil } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { environment } from '../../../../../environments/environment';
import { AppExceptionModel } from '../../../../models/app-exception.model';
import { Grade } from '../../../../models/grade.school.model';
import { Group } from '../../../../models/group.model';
import {
  Lesson,
  LessonStatus,
  NotesType,
} from '../../../../models/lesson.school.model';
import { UserModel } from '../../../../models/user.model';
import { SchoolLessonService } from '../../../../services/lesson.school.service';
import { UtilsService } from '../../../../services/utils.service';
import * as lessonActions from '../../../../state/actions/lesson.school.actions';
import { AppState } from '../../../../state/appstate';
import * as gradeSelector from '../../../../state/selectors/grade.school.selector';
import * as groupSelector from '../../../../state/selectors/group.selector';
import { BtnSubmitComponent } from '../../../components/buttons/btn-submit/btn-submit.component';
import { DlgConfirmComponent } from '../../../components/dialogs/dlg-confirm/dlg-confirm.component';
import { S3DocumentUploadComponent } from '../../../components/uploaders/s3-document-upload/s3-document-upload.component';

@Component({
  selector: 'app-dlg-upsert-lesson',
  standalone: true,
  imports: [
    BtnSubmitComponent,
    AsyncPipe,
    ReactiveFormsModule,
    JsonPipe,
    S3DocumentUploadComponent,
  ],
  templateUrl: './dlg-upsert-lesson.component.html',
  styleUrl: './dlg-upsert-lesson.component.css',
})
export class DlgUpsertLessonComponent implements OnInit, OnDestroy {
  @ViewChild('uploader') uploader: S3DocumentUploadComponent | undefined;

  store: Store<AppState> = inject(Store<AppState>);
  toastr: ToastrService = inject(ToastrService);
  lessonService: SchoolLessonService = inject(SchoolLessonService);
  readonly dialogRef = inject(MatDialogRef<DlgUpsertLessonComponent>);
  private dialog: MatDialog = inject(MatDialog);
  private actions$: Actions = inject(Actions);

  readonly data = inject<any>(MAT_DIALOG_DATA);
  fb = inject(FormBuilder);

  uuid = uuidv4();

  user: UserModel | null = null;
  groupId: string = this.data.groupId;
  group: Group | undefined = undefined;
  grades: Grade[] = [];

  pageForm: FormGroup;

  tags: string[] = [];

  preSignedUrlEndpoint: string = environment.AWS_SCHOOL_LESSON_SIGNEDURL;
  apiKey: string = environment.AWS_API_KEY;
  metadata: any = {};
  cloudFrontDomain = environment.AWS_GROUPS_CLOUDFRONT;

  title: string = 'Create Lesson';
  description: string = 'Enter Lesson Details';
  action: string = 'Create';
  gradeReadOnly: boolean = false;
  lessonStatusFixed: boolean = false;

  lessonStatus = LessonStatus;
  notesTypeList = [NotesType.pdf, NotesType.text];
  notesType = NotesType;

  loading$: Observable<boolean> = of(false);

  myRoles$: Observable<any> = new Observable();
  exit$: Subject<void> = new Subject<void>();

  constructor() {
    this.pageForm = this.fb.group({
      lessonName: new FormControl(this.data.lesson?.lessonName, [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(100),
      ]),
      gradeId: new FormControl(this.data.lesson?.gradeId ?? this.data.gradeId, [
        Validators.required,
      ]),
      avgAge: new FormControl(this.data.lesson?.avgAge ?? this.data.avgAge, [
        Validators.required,
      ]),
      lessonStatus: new FormControl(
        this.data.lesson?.lessonStatus ?? LessonStatus.available,
      ),
      notesType: new FormControl(NotesType.pdf),
      tagField: new FormControl(this.data.lesson?.tags),
    });

    this.tags = this.data.lesson?.tags ?? [];

    this.title = this.data.title;
    this.description = this.data.description;
    this.action = this.data.action;
    this.gradeReadOnly = this.data.gradeReadOnly;
    this.lessonStatusFixed = this.data.lessonStatusFixed;
  }

  ngOnInit(): void {
    this.store
      .select((state) => state.user.user)
      .pipe(takeUntil(this.exit$))
      .subscribe((user) => {
        this.user = user;
      });

    this.store
      .select(gradeSelector.getGradesByGroup(this.groupId))
      .pipe(takeUntil(this.exit$))
      .subscribe((grades) => {
        this.grades = grades;
        if (this.data.lesson?.gradeId) {
          const grade = this.grades.find(
            (g) => g.id === this.data.lesson?.gradeId,
          );
          this.pageForm.get('avgAge')?.setValue(grade?.gradeAvgAge);
        }
      });

    this.store
      .select(gradeSelector.getGradesByGroup(this.groupId))
      .pipe(takeUntil(this.exit$))
      .subscribe((grades) => {
        this.grades = grades;
        this.setFormGrade();
      });

    this.store
      .select(groupSelector.getSelectedGroup)
      .pipe(takeUntil(this.exit$))
      .subscribe((group) => {
        this.group = group;
        this.setFormGrade();
      });

    this.actions$
      .pipe(ofType(lessonActions.createSuccess), takeUntil(this.exit$))
      .subscribe((action) => {
        this.loading$ = of(false);
        this.dialogRef.close(action.lesson);
      });

    this.actions$
      .pipe(ofType(lessonActions.createFailure), takeUntil(this.exit$))
      .subscribe((action) => {
        this.loading$ = of(false);
        this.onShowAlert(action.error as AppExceptionModel);
      });
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  setFormGrade() {
    if (
      this.group?.gradeId &&
      this.myRoles$
        .pipe(take(1))
        .subscribe((roles) => roles.management || roles.faculty)
    ) {
      this.pageForm.get('gradeId')?.setValue(this.group?.gradeId);
      if (this.group?.gradeId) {
        const grade = this.grades.find((g) => g.id === this.group?.gradeId);
        this.pageForm.get('avgAge')?.setValue(grade?.gradeAvgAge);
      }
    }
  }

  addTag() {
    this.pageForm.get('tagField')?.setErrors(null);
    if (this.pageForm.get('tagField')?.value === null) return;
    const fieldValue: string = this.pageForm.get('tagField')?.value.trim();
    if (fieldValue === '') return;
    if (fieldValue.includes(' ')) {
      console.log(`Invalid Tag: ${fieldValue}`);
      return this.pageForm.controls['tagField'].setErrors({
        invalidValue: true,
      });
    }
    if ((this.tags.length ?? 0) >= 10) {
      console.log(`Invalid Tag: ${fieldValue}`);
      return this.pageForm.controls['tagField'].setErrors({
        maxValuesAllowed: true,
      });
    }
    this.tags = [...this.tags, fieldValue];
    this.pageForm.get('tagField')?.patchValue('');
    this.pageForm.get('tagField')?.markAsPristine();
  }

  removeTag(event: any) {
    this.tags.splice(event.target.value, 1);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onNotesTypeChange(event: any) {
    const selectedType: NotesType = event.target.value;
    this.pageForm.patchValue({ notesType: selectedType });
  }

  async submit() {
    if (
      this.pageForm.invalid ||
      (this.pageForm.get('notesType')?.value === NotesType.pdf &&
        !this.uploader?.getIsAttached())
    ) {
      return;
    }

    this.loading$ = of(true);

    if (this.pageForm.get('notesType')?.value === NotesType.pdf) {
      this.uploader?.setInputParams({
        groupId: this.groupId,
        id: this.uuid,
      });
      this.uploader?.setMetadata({
        groupId: this.groupId,
        id: this.uuid,
      });
      this.uploader
        ?.upload()
        ?.then((result) => {
          console.log('Upload Result:', result);
          if (result?.successful) {
            this.createDatabaseEntry(
              this.uuid,
              result.successful[0]?.response?.uploadURL,
            );
          } else {
            this.loading$ = of(false);
            this.toastr.error(
              `Failed to upload document. ${result?.failed}`,
              'Document Upload Error',
            );
          }
        })
        .catch((error) => {
          this.loading$ = of(false);
          this.onShowAlert(error as AppExceptionModel);
        });
    } else {
      await this.createDatabaseEntry(this.uuid);
    }
  }

  async createDatabaseEntry(id: string, url?: string | undefined) {
    try {
      if (this.data.lesson?.id == undefined) {
        const lesson: Lesson = {
          lessonName: this.pageForm.get('lessonName')?.value,
          lessonStatus: this.pageForm.get('lessonStatus')?.value,
          notesType: this.pageForm.get('notesType')?.value,
          groupId: this.data.groupId,
          gradeId: this.pageForm.get('gradeId')?.value,
          avgAge: this.pageForm.get('avgAge')?.value,
          tags: this.tags,
          createdBy: this.user?.name,
          createdById: this.user?.uid,
          created: Timestamp.now(),
          modified: Timestamp.now(),
        } as Lesson;

        if (url != undefined) {
          lesson.notesUrl = UtilsService.replaceDomain(
            url,
            this.cloudFrontDomain,
          );
        }

        // console.debug(
        //   `DlgUpsertLessonComponent :: submit: Create lesson: ${JSON.stringify(
        //     lesson,
        //     null,
        //     2
        //   )}`
        // );
        this.store.dispatch(
          lessonActions.create({
            groupId: this.groupId!,
            lesson: {
              ...lesson,
              created: Timestamp.now(),
              modified: Timestamp.now(),
            } as Lesson,
          }),
        );
      } else {
        const lesson: Lesson = {
          ...this.data.lesson,
          lessonName: this.pageForm.get('lessonName')?.value,
          lessonStatus: this.pageForm.get('lessonStatus')?.value,
          notesType: this.pageForm.get('notesType')?.value,
          gradeId: this.pageForm.get('gradeId')?.value,
          avgAge: this.pageForm.get('avgAge')?.value,
          tags: this.tags,
          modified: Timestamp.now(),
        } as Lesson;

        if (url != undefined) {
          lesson.notesUrl = UtilsService.replaceDomain(
            url,
            this.cloudFrontDomain,
          );
        }

        // console.debug(
        //   `DlgUpsertLessonComponent :: submit: Updating lesson: ${JSON.stringify(
        //     lesson,
        //     null,
        //     2
        //   )}`
        // );

        this.store.dispatch(
          lessonActions.update({
            groupId: this.groupId!,
            lessonId: this.data.lesson.id,
            lesson: {
              ...lesson,
              created: Timestamp.now(),
              modified: Timestamp.now(),
            } as Lesson,
          }),
        );
      }
    } catch (error) {
      this.onShowAlert(error as AppExceptionModel);
    }
    this.loading$ = of(false);
  }

  onGradeChange(event: any) {
    const grade = this.grades.find((g) => g.id === event.target.value);
    this.pageForm.get('avgAge')?.setValue(grade?.gradeAvgAge);
  }

  onShowAlert(error: AppExceptionModel) {
    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      title: error.title,
      description: error.message,
      action: 'Okay',
      buttonColor: 'bg-primary-500',
      showType: 'error',
    };

    this.dialog.open(DlgConfirmComponent, dialogConfig);

    return;
  }
}
